"use client"

import { useState } from "react"
import {
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  addDoc,
  collection,
  getFirestore,
  serverTimestamp,
} from "firebase/firestore"
import { useAuth } from "../context/AuthContext"
import { db } from "../firebase"
import { v4 as uuidv4 } from "uuid"
import { createTransaction } from "../database/Functions"
import { AlertModal } from "./ui/alert-modal"
import { getTeamLogoWithFallback } from "../utils/teamLogoUtils"

function BetslipItem({ bet }) {
  const [wager, setWager] = useState("")
  const [toWin, setToWin] = useState(0)
  const [alertModal, setAlertModal] = useState({
    open: false,
    title: "",
    description: "",
  })
  const [createdBetShareLink, setCreatedBetShareLink] = useState("")
  const [shareLinkCopied, setShareLinkCopied] = useState(false)
  const { currentUser, userProfile, systemSettings } = useAuth()
  const sportKey = bet?.bet_sport_key || bet?.sport_key
  const homeLogo = bet?.home_team ? getTeamLogoWithFallback(bet.home_team, sportKey) : ""
  const awayLogo = bet?.away_team ? getTeamLogoWithFallback(bet.away_team, sportKey) : ""

  const openAlertModal = (title, description) => {
    setAlertModal({
      open: true,
      title,
      description,
    })
  }

  const handleCopyCreatedBetLink = async () => {
    if (!createdBetShareLink) return
    try {
      await navigator.clipboard.writeText(createdBetShareLink)
      setShareLinkCopied(true)
    } catch (err) {
      console.error("Failed to copy created bet link:", err)
    }
  }

  const handleWagerChange = (e) => {
    const wagerValue = Number.parseFloat(e.target.value)
    setWager(e.target.value)
    if (!isNaN(wagerValue)) {
      setToWin((wagerValue * 0.8).toFixed(2))
    } else {
      setToWin(0)
      setToWin(0)
    }
  }

  const findSimilarBets = async () => {

    if(wager <=0){
      alert("Please enter a valid wager amount.")
      return
    }

    if (
      !systemSettings ||
      systemSettings.betsAllowed === false ||
      systemSettings.canPlaceBets === false ||
      systemSettings.blockAppUse === true
    ) {
      alert("Bets are temporarily blocked.")
      return
    }

    const account_balance = userProfile.account_balance

    if ( account_balance >= wager * 1.2) {
      const uid = currentUser.uid
      const min = wager * 0.8
      const max = wager * 1.2

      const q = query(collection(db, "ActiveBets"), where("bet_id", "==", bet.id), where("accepted", "==", "No"))

      const querySnapshot = await getDocs(q)
      const results = querySnapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }))

      const similarBets = results.filter(
        (betData) =>
          betData.user_id !== uid &&
          betData.user_pick !== bet.user_pick &&
          betData.wager_amount >= min &&
          betData.wager_amount <= max &&
          betData.status !== "Deleted",
      )

      if (similarBets.length > 0) {
        const similarBet = similarBets[0]
        const message = `Someone has chosen ${similarBet.user_pick} to win $${similarBet.wager_amount}. Do you want to accept this bet instead of creating a new one?`

        const message2 = `Do you want to create your own bet instead for $${wager}`

        if (window.confirm(message)) {
          await acceptBet(similarBet)
        } else {
          if (window.confirm(message2)) {
            createBet()
          }
        }
      } else {
        createBet()
      }
    } else {
      alert("Insufficient Funds. Please deposit more money.")
    }
  }

  const acceptBet = async (bet) => {
    const uid = currentUser.uid
    var beforeBalance = userProfile.account_balance
    const account_balance = userProfile.account_balance - wager
    var afterBalance = account_balance

    const data = {
      accepted: "Yes",
      bet_acceptor_uid: uid,
      status: "Active",
    }

    await updateDoc(doc(db, "ActiveBets", bet.id), data)
    await updateDoc(doc(db, "users", uid), { account_balance })
    createTransaction(uid, "Wager", "Accept Wager", beforeBalance, afterBalance, wager, data)
    setCreatedBetShareLink("")
    setShareLinkCopied(false)
    openAlertModal(
      "Bet Placed Successfully",
      "Your wager is now live in the marketplace.\n\nOnce another user accepts this bet, it will move to Active Bets and update in your My Bets tab."
    )
  }

  const createBet = async () => {
    const db = getFirestore()
    const uniqueId = uuidv4()

    if (currentUser) {
      const uid = currentUser.uid
      const docRef = doc(db, "users", uid)

      let account_balance = userProfile.account_balance
      var beforeBalance = account_balance

      if(wager <=0){
        alert("Please enter a valid wager amount.")
        return
      }

      if (account_balance >= wager) {
        account_balance -= wager
        var afterBalance = account_balance
        const data = {
          user_id: uid,
          bet_id: bet.id,
          user_pick: bet.user_pick,
          opponent_pick: bet.user_pick === bet.home_team ? bet.away_team : bet.home_team,
          home_team: bet.home_team,
          away_team: bet.away_team,
          status: "Created",
          paid_out: "No",
          bet_acceptor_uid: null,
          wager_amount: Number.parseFloat(wager),
          payout: (Number.parseFloat(wager) + Number.parseFloat(toWin)).toFixed(2),
          bet_sport_key: bet.sport_key,
          bet_sport_title: bet.sport_title,
          bet_time: bet.commence_time,
          accepted: "No",
          uniqueId: uniqueId,
          winner: "",
          user_name: userProfile?.username || userProfile?.full_name,
          createdAt: serverTimestamp(),
        }

        await addDoc(collection(db, "ActiveBets"), data)
        await updateDoc(docRef, { account_balance })
        createTransaction(uid, "Wager", "Create Wager", beforeBalance, afterBalance, wager, data)
        const shareUrl = `https://www.rivalssportsbook.com/betdetails?id=${uniqueId}`
        setCreatedBetShareLink(shareUrl)
        setShareLinkCopied(false)
        openAlertModal(
          "Bet Placed Successfully",
          "Your bet has been created and is currently in \"Created\" status. It stays open until another user accepts it. Once accepted, it automatically moves to Active Bets and both users are locked into the wager.\n\nShare this bet with friends so they can open it and accept faster.\nTap \"Copy Share Link\" below, then paste it in text or social."
        )

        // 🟣 Send email through Formspree
        try {
          await fetch("https://formspree.io/f/mldpagdv", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              subject: `New Bet Created by ${userProfile?.full_name}`,
              message: `
                A new bet has been created:
                User: ${userProfile?.full_name}
                Wager: $${wager}
                To Win: $${toWin}
                Teams: ${bet.home_team} vs ${bet.away_team}
                Pick: ${bet.user_pick}
                Sport: ${bet.sport_title}
                Time: ${bet.commence_time}
                Bet ID: ${uniqueId}
              `,
              email: userProfile?.email || "unknown",
            }),
          })
        } catch (error) {
          console.log("Error sending email:", error)
        }
        
        setWager(0)
        setToWin(0)
      } else {
        alert("Insufficient Funds. Please deposit more money.")
        setWager(0)
        setToWin(0)
      }
    }
    setWager(0)
    setToWin(0)
  }

  function clearBets() {
    bet = null
  }

  return (
    <div className="flex flex-col rounded-xl bg-gray-900/80 backdrop-blur-sm w-full border border-gray-800">
      {/* Betslip Item */}
      {bet && (
        <div className="p-4 xl:p-5 w-full mx-auto mb-3 text-white bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center space-x-2">
              <div className="bg-emerald-500/20 p-2 rounded-lg">
                <span className="text-emerald-400 font-bold">VS</span>
              </div>
              <div>
                <h3 className="text-xs xl:text-sm font-semibold leading-tight">
                  <span className="inline-flex items-center gap-1.5 mr-1">
                    <img
                      src={homeLogo}
                      alt={bet.home_team}
                      className="w-4 h-4 rounded-full bg-gray-900/60 border border-gray-700 object-contain p-0.5"
                      onError={(e) => {
                        e.currentTarget.onerror = null
                        e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(bet.home_team)}&background=1f2937&color=ffffff&size=96`
                      }}
                    />
                    <span>{bet.home_team}</span>
                  </span>
                  <span className="mx-1">vs</span>
                  <span className="inline-flex items-center gap-1.5 ml-1">
                    <img
                      src={awayLogo}
                      alt={bet.away_team}
                      className="w-4 h-4 rounded-full bg-gray-900/60 border border-gray-700 object-contain p-0.5"
                      onError={(e) => {
                        e.currentTarget.onerror = null
                        e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(bet.away_team)}&background=1f2937&color=ffffff&size=96`
                      }}
                    />
                    <span>{bet.away_team}</span>
                  </span>
                </h3>
                <p className="text-xs text-gray-400">Your pick: {bet.user_pick}</p>
              </div>
            </div>
            <span className="text-emerald-400 font-semibold text-sm">-120</span>
          </div>

          <div className="bg-gray-700/30 p-3 rounded-lg mb-4">
            <div className="flex justify-between mb-2">
              <span className="text-xs text-gray-300">Potential Payout:</span>
              <span className="text-base font-semibold text-emerald-400">
                ${wager && toWin ? (Number.parseFloat(wager) + Number.parseFloat(toWin)).toFixed(2) : "0.00"}
              </span>
            </div>
          </div>

          {/* Wager and To Win inputs */}
          <div className="grid grid-cols-2 gap-2 xl:gap-3 mb-4 items-start">
            <div className="space-y-1 min-w-0">
              <label className="text-xs text-gray-300">Wager Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="number"
                  min="0"
                  placeholder="0.00"
                  value={wager}
                  onChange={handleWagerChange}
                  className="w-full h-10 p-2 pl-8 rounded-lg bg-gray-900/70 border border-gray-700 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="space-y-1 min-w-0">
              <label className="text-xs text-gray-300">Potential Win</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="number"
                  placeholder="0.00"
                  value={toWin}
                  readOnly
                  className="w-full h-10 p-2 pl-8 rounded-lg bg-gray-900/70 border border-gray-700 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Create Bet button */}
          <div className="space-y-2">
            <button
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white py-2.5 rounded-lg text-sm font-medium transition-colors"
              onClick={findSimilarBets}
            >
              Create Bet
            </button>
            <button
              className="w-full bg-gray-800 border border-gray-700 hover:bg-gray-700 text-white py-2.5 rounded-lg text-sm font-medium transition-colors"
              onClick={clearBets}
            >
              Clear Bet
            </button>
          </div>
        </div>
      )}
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        description={
          shareLinkCopied
            ? `${alertModal.description}\n\nLink copied to clipboard:\n${createdBetShareLink}`
            : alertModal.description
        }
        secondaryActionLabel={createdBetShareLink ? "Copy Share Link" : ""}
        onSecondaryAction={createdBetShareLink ? handleCopyCreatedBetLink : undefined}
        onClose={() =>
          {
            setAlertModal((prev) => ({
              ...prev,
              open: false,
            }))
            setShareLinkCopied(false)
          }
        }
      />
    </div>
  )
}

export default BetslipItem

